import supabase from '../lib/supabase';
import { addDays, differenceInDays, format, addMonths, addYears } from 'date-fns';
import invoiceService from './invoiceService';

// Demo subscription plans
const demoPlans = [
  {
    id: 1,
    name: 'Basic SEO',
    description: 'Essential SEO services for small businesses',
    price: 299,
    billing_cycle: 'monthly',
    features: [
      'Keyword Research',
      'On-Page Optimization',
      'Monthly Reports',
      'Email Support'
    ],
    trial_days: 7,
    category: 'SEO',
    is_active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 2,
    name: 'Pro SEO',
    description: 'Advanced SEO with content marketing',
    price: 599,
    billing_cycle: 'monthly',
    features: [
      'Everything in Basic',
      'Content Strategy',
      'Link Building',
      'Competitor Analysis',
      'Priority Support'
    ],
    trial_days: 14,
    category: 'SEO',
    is_active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 3,
    name: 'Enterprise SEO',
    description: 'Complete SEO solution for large businesses',
    price: 1299,
    billing_cycle: 'monthly',
    features: [
      'Everything in Pro',
      'Technical SEO Audit',
      'Custom Strategy',
      'Dedicated Account Manager',
      '24/7 Support'
    ],
    trial_days: 30,
    category: 'SEO',
    is_active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 4,
    name: 'PPC Starter',
    description: 'Google Ads management for beginners',
    price: 499,
    billing_cycle: 'monthly',
    features: [
      'Campaign Setup',
      'Keyword Management',
      'Monthly Optimization',
      'Performance Reports'
    ],
    trial_days: 7,
    category: 'PPC',
    is_active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 5,
    name: 'Social Media Pro',
    description: 'Complete social media management',
    price: 799,
    billing_cycle: 'monthly',
    features: [
      'Content Creation',
      'Daily Posting',
      'Community Management',
      'Analytics Reports',
      'Ad Campaign Management'
    ],
    trial_days: 14,
    category: 'Social Media',
    is_active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 6,
    name: 'Annual SEO Pro',
    description: 'Pro SEO with annual discount',
    price: 5990,
    billing_cycle: 'yearly',
    features: [
      'Everything in Pro SEO',
      '2 months free',
      'Annual strategy review'
    ],
    trial_days: 30,
    category: 'SEO',
    is_active: true,
    created_at: '2024-01-01T00:00:00Z'
  }
];

// Demo subscriptions
const demoSubscriptions = [
  {
    id: 1,
    client_id: 1,
    plan_id: 2,
    client: {
      name: 'John Smith',
      business_name: 'TechCorp Ltd',
      email: 'john@techcorp.com'
    },
    plan: demoPlans[1],
    status: 'active',
    start_date: '2024-01-15',
    end_date: '2024-02-15',
    next_billing_date: '2024-02-15',
    trial_start: '2024-01-01',
    trial_end: '2024-01-15',
    is_trial: false,
    auto_renew: true,
    payment_method: 'credit_card',
    discount_percentage: 0,
    custom_price: null,
    notes: 'Upgraded from Basic plan',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-15T00:00:00Z'
  },
  {
    id: 2,
    client_id: 2,
    plan_id: 1,
    client: {
      name: 'Michael Brown',
      business_name: 'StartupXYZ',
      email: 'mike@startupxyz.com'
    },
    plan: demoPlans[0],
    status: 'trial',
    start_date: '2024-01-20',
    end_date: '2024-01-27',
    next_billing_date: '2024-01-27',
    trial_start: '2024-01-20',
    trial_end: '2024-01-27',
    is_trial: true,
    auto_renew: true,
    payment_method: 'bank_transfer',
    discount_percentage: 10,
    custom_price: null,
    notes: 'New client trial',
    created_at: '2024-01-20T00:00:00Z',
    updated_at: '2024-01-20T00:00:00Z'
  },
  {
    id: 3,
    client_id: 3,
    plan_id: 4,
    client: {
      name: 'Lisa Anderson',
      business_name: 'E-commerce Plus',
      email: 'lisa@ecommerceplus.com'
    },
    plan: demoPlans[3],
    status: 'paused',
    start_date: '2023-12-01',
    end_date: '2024-01-01',
    next_billing_date: '2024-02-01',
    trial_start: null,
    trial_end: null,
    is_trial: false,
    auto_renew: false,
    payment_method: 'paypal',
    discount_percentage: 0,
    custom_price: 450,
    notes: 'Custom pricing due to budget constraints',
    created_at: '2023-12-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  }
];

class SubscriptionService {
  constructor() {
    this.isOnline = false;
    this.checkConnection();
  }

  async checkConnection() {
    try {
      const { data, error } = await supabase.from('subscriptions_2024').select('count', { count: 'exact' });
      this.isOnline = !error;
    } catch (error) {
      this.isOnline = false;
    }
  }

  calculateNextBillingDate(startDate, billingCycle) {
    const start = new Date(startDate);
    
    switch (billingCycle) {
      case 'weekly':
        return addDays(start, 7);
      case 'monthly':
        return addMonths(start, 1);
      case 'quarterly':
        return addMonths(start, 3);
      case 'yearly':
        return addYears(start, 1);
      default:
        return addMonths(start, 1);
    }
  }

  calculateTrialEndDate(startDate, trialDays) {
    return addDays(new Date(startDate), trialDays);
  }

  async getAllPlans() {
    if (!this.isOnline) {
      return { data: demoPlans, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('subscription_plans_2024')
        .select('*')
        .eq('is_active', true)
        .order('price', { ascending: true });

      return { data: data || [], error };
    } catch (error) {
      return { data: demoPlans, error: null };
    }
  }

  async getPlan(id) {
    if (!this.isOnline) {
      const plan = demoPlans.find(p => p.id === parseInt(id));
      return { data: plan, error: plan ? null : 'Plan not found' };
    }

    try {
      const { data, error } = await supabase
        .from('subscription_plans_2024')
        .select('*')
        .eq('id', id)
        .single();

      return { data, error };
    } catch (error) {
      const plan = demoPlans.find(p => p.id === parseInt(id));
      return { data: plan, error: plan ? null : 'Plan not found' };
    }
  }

  async getAllSubscriptions() {
    if (!this.isOnline) {
      return { data: demoSubscriptions, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions_2024')
        .select(`
          *,
          client:clients_billing_2024(*),
          plan:subscription_plans_2024(*)
        `)
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      return { data: demoSubscriptions, error: null };
    }
  }

  async getSubscription(id) {
    if (!this.isOnline) {
      const subscription = demoSubscriptions.find(s => s.id === parseInt(id));
      return { data: subscription, error: subscription ? null : 'Subscription not found' };
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions_2024')
        .select(`
          *,
          client:clients_billing_2024(*),
          plan:subscription_plans_2024(*)
        `)
        .eq('id', id)
        .single();

      return { data, error };
    } catch (error) {
      const subscription = demoSubscriptions.find(s => s.id === parseInt(id));
      return { data: subscription, error: subscription ? null : 'Subscription not found' };
    }
  }

  async createSubscription(subscriptionData) {
    const plan = demoPlans.find(p => p.id === subscriptionData.plan_id);
    if (!plan) {
      return { data: null, error: 'Plan not found' };
    }

    const startDate = new Date();
    const trialEndDate = subscriptionData.start_trial ? 
      this.calculateTrialEndDate(startDate, plan.trial_days) : startDate;
    
    const billingStartDate = subscriptionData.start_trial ? trialEndDate : startDate;
    const nextBillingDate = this.calculateNextBillingDate(billingStartDate, plan.billing_cycle);

    const newSubscription = {
      ...subscriptionData,
      start_date: startDate.toISOString(),
      end_date: nextBillingDate.toISOString(),
      next_billing_date: nextBillingDate.toISOString(),
      trial_start: subscriptionData.start_trial ? startDate.toISOString() : null,
      trial_end: subscriptionData.start_trial ? trialEndDate.toISOString() : null,
      is_trial: subscriptionData.start_trial || false,
      status: subscriptionData.start_trial ? 'trial' : 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    if (!this.isOnline) {
      newSubscription.id = Math.max(...demoSubscriptions.map(s => s.id)) + 1;
      newSubscription.client = subscriptionData.client;
      newSubscription.plan = plan;
      demoSubscriptions.unshift(newSubscription);
      return { data: newSubscription, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions_2024')
        .insert([newSubscription])
        .select(`
          *,
          client:clients_billing_2024(*),
          plan:subscription_plans_2024(*)
        `)
        .single();

      return { data, error };
    } catch (error) {
      newSubscription.id = Math.max(...demoSubscriptions.map(s => s.id)) + 1;
      newSubscription.client = subscriptionData.client;
      newSubscription.plan = plan;
      demoSubscriptions.unshift(newSubscription);
      return { data: newSubscription, error: null };
    }
  }

  async updateSubscription(id, subscriptionData) {
    const updatedData = {
      ...subscriptionData,
      updated_at: new Date().toISOString()
    };

    if (!this.isOnline) {
      const index = demoSubscriptions.findIndex(s => s.id === parseInt(id));
      if (index !== -1) {
        demoSubscriptions[index] = { ...demoSubscriptions[index], ...updatedData };
        return { data: demoSubscriptions[index], error: null };
      }
      return { data: null, error: 'Subscription not found' };
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions_2024')
        .update(updatedData)
        .eq('id', id)
        .select(`
          *,
          client:clients_billing_2024(*),
          plan:subscription_plans_2024(*)
        `)
        .single();

      return { data, error };
    } catch (error) {
      const index = demoSubscriptions.findIndex(s => s.id === parseInt(id));
      if (index !== -1) {
        demoSubscriptions[index] = { ...demoSubscriptions[index], ...updatedData };
        return { data: demoSubscriptions[index], error: null };
      }
      return { data: null, error: 'Subscription not found' };
    }
  }

  async cancelSubscription(id, reason = '') {
    return await this.updateSubscription(id, {
      status: 'cancelled',
      auto_renew: false,
      notes: reason ? `Cancelled: ${reason}` : 'Cancelled by user'
    });
  }

  async pauseSubscription(id, reason = '') {
    return await this.updateSubscription(id, {
      status: 'paused',
      notes: reason ? `Paused: ${reason}` : 'Paused by user'
    });
  }

  async resumeSubscription(id) {
    const { data: subscription } = await this.getSubscription(id);
    if (!subscription) {
      return { data: null, error: 'Subscription not found' };
    }

    const nextBillingDate = this.calculateNextBillingDate(
      new Date(), 
      subscription.plan.billing_cycle
    );

    return await this.updateSubscription(id, {
      status: 'active',
      next_billing_date: nextBillingDate.toISOString()
    });
  }

  async upgradeSubscription(id, newPlanId, prorated = true) {
    const { data: subscription } = await this.getSubscription(id);
    const { data: newPlan } = await this.getPlan(newPlanId);
    
    if (!subscription || !newPlan) {
      return { data: null, error: 'Subscription or plan not found' };
    }

    // Calculate prorated amount if needed
    let proratedCredit = 0;
    if (prorated && subscription.plan) {
      const daysRemaining = differenceInDays(
        new Date(subscription.next_billing_date),
        new Date()
      );
      const totalDays = subscription.plan.billing_cycle === 'monthly' ? 30 : 365;
      proratedCredit = (subscription.plan.price / totalDays) * daysRemaining;
    }

    const nextBillingDate = this.calculateNextBillingDate(new Date(), newPlan.billing_cycle);

    const result = await this.updateSubscription(id, {
      plan_id: newPlanId,
      next_billing_date: nextBillingDate.toISOString(),
      notes: `Upgraded from ${subscription.plan.name} to ${newPlan.name}${prorated ? ` (Prorated credit: $${proratedCredit.toFixed(2)})` : ''}`
    });

    // Generate prorated invoice if applicable
    if (prorated && proratedCredit > 0) {
      // This would create a credit note or adjust the next invoice
      console.log(`Prorated credit: $${proratedCredit.toFixed(2)}`);
    }

    return result;
  }

  async processRenewal(subscriptionId) {
    const { data: subscription } = await this.getSubscription(subscriptionId);
    if (!subscription) {
      return { data: null, error: 'Subscription not found' };
    }

    if (!subscription.auto_renew || subscription.status !== 'active') {
      return { data: null, error: 'Subscription not eligible for renewal' };
    }

    // Calculate next billing date
    const nextBillingDate = this.calculateNextBillingDate(
      subscription.next_billing_date,
      subscription.plan.billing_cycle
    );

    // Create invoice for the renewal
    const invoiceData = {
      client_id: subscription.client_id,
      client: subscription.client,
      invoice_type: 'recurring',
      status: 'sent',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: addDays(new Date(), 30).toISOString().split('T')[0],
      services: [{
        name: subscription.plan.name,
        description: `${subscription.plan.billing_cycle.charAt(0).toUpperCase() + subscription.plan.billing_cycle.slice(1)} subscription`,
        quantity: 1,
        rate: subscription.custom_price || subscription.plan.price,
        amount: subscription.custom_price || subscription.plan.price,
        tax_rate: subscription.client.gst_type === 'B2C' ? 0 : 18
      }],
      payment_terms: 'Net 30 days',
      notes: `Automatic renewal - Subscription ID: ${subscriptionId}`,
      subscription_id: subscriptionId
    };

    try {
      // Create the renewal invoice
      const { data: invoice, error: invoiceError } = await invoiceService.createInvoice(invoiceData);
      
      if (invoiceError) {
        return { data: null, error: 'Failed to create renewal invoice' };
      }

      // Update subscription for next billing cycle
      const { data: updatedSubscription, error: updateError } = await this.updateSubscription(subscriptionId, {
        next_billing_date: nextBillingDate.toISOString(),
        end_date: nextBillingDate.toISOString()
      });

      return { 
        data: { 
          subscription: updatedSubscription, 
          invoice 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: 'Failed to process renewal' };
    }
  }

  async getSubscriptionsByClient(clientId) {
    const { data: subscriptions } = await this.getAllSubscriptions();
    return {
      data: subscriptions.filter(sub => sub.client_id === parseInt(clientId)),
      error: null
    };
  }

  async getExpiringTrials(daysAhead = 3) {
    const { data: subscriptions } = await this.getAllSubscriptions();
    const cutoffDate = addDays(new Date(), daysAhead);
    
    return {
      data: subscriptions.filter(sub => 
        sub.is_trial && 
        sub.status === 'trial' &&
        new Date(sub.trial_end) <= cutoffDate
      ),
      error: null
    };
  }

  async getUpcomingRenewals(daysAhead = 7) {
    const { data: subscriptions } = await this.getAllSubscriptions();
    const cutoffDate = addDays(new Date(), daysAhead);
    
    return {
      data: subscriptions.filter(sub => 
        sub.auto_renew && 
        sub.status === 'active' &&
        new Date(sub.next_billing_date) <= cutoffDate
      ),
      error: null
    };
  }

  async getSubscriptionStatistics() {
    const { data: subscriptions } = await this.getAllSubscriptions();
    
    const stats = {
      total_subscriptions: subscriptions.length,
      active_subscriptions: subscriptions.filter(s => s.status === 'active').length,
      trial_subscriptions: subscriptions.filter(s => s.status === 'trial').length,
      paused_subscriptions: subscriptions.filter(s => s.status === 'paused').length,
      cancelled_subscriptions: subscriptions.filter(s => s.status === 'cancelled').length,
      monthly_recurring_revenue: subscriptions
        .filter(s => s.status === 'active' && s.plan?.billing_cycle === 'monthly')
        .reduce((sum, s) => sum + (s.custom_price || s.plan?.price || 0), 0),
      annual_recurring_revenue: subscriptions
        .filter(s => s.status === 'active')
        .reduce((sum, s) => {
          const price = s.custom_price || s.plan?.price || 0;
          const multiplier = s.plan?.billing_cycle === 'yearly' ? 1 : 
                           s.plan?.billing_cycle === 'monthly' ? 12 : 
                           s.plan?.billing_cycle === 'quarterly' ? 4 : 12;
          return sum + (price * multiplier);
        }, 0),
      expiring_trials: subscriptions.filter(s => 
        s.is_trial && 
        s.status === 'trial' &&
        new Date(s.trial_end) <= addDays(new Date(), 3)
      ).length,
      upcoming_renewals: subscriptions.filter(s => 
        s.auto_renew && 
        s.status === 'active' &&
        new Date(s.next_billing_date) <= addDays(new Date(), 7)
      ).length
    };

    return { data: stats, error: null };
  }

  async convertTrialToSubscription(subscriptionId, planId = null) {
    const { data: subscription } = await this.getSubscription(subscriptionId);
    if (!subscription) {
      return { data: null, error: 'Subscription not found' };
    }

    if (!subscription.is_trial) {
      return { data: null, error: 'Subscription is not in trial' };
    }

    const targetPlanId = planId || subscription.plan_id;
    const nextBillingDate = this.calculateNextBillingDate(new Date(), subscription.plan.billing_cycle);

    return await this.updateSubscription(subscriptionId, {
      plan_id: targetPlanId,
      status: 'active',
      is_trial: false,
      trial_start: null,
      trial_end: null,
      next_billing_date: nextBillingDate.toISOString(),
      end_date: nextBillingDate.toISOString()
    });
  }
}

export default new SubscriptionService();